import React, { useState, useEffect, useCallback } from 'react';
import './DayClose.css';
import fetchData from '../../functions/fetchData';
import Layout from '../../layout/Layout';
import FloatingNavModal from '../../components/floatingbuttons/FloatingNavModal';
import { getLastCloseDate } from '../../api/dayCloseApi';
import { getHeaders } from '../../utility/getHeader';
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { 
  FaCalendarDay, 
  FaCalendarAlt, 
  FaFilePdf,
  FaFileExcel,
  FaPrint,
  FaCheckCircle,
  FaExclamationTriangle,
  FaMoneyBillWave,
  FaMoneyBillAlt
} from 'react-icons/fa';
import { Button, Spinner, Alert, Row, Col, Form, Modal, Container } from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';

const DayClose = () => {
  
  const [selectedDate, setSelectedDate] = useState('');
  const [salesData, setSalesData] = useState({
    totalSales: 0,
    totalDiscount: 0,
    cashAmount: 0,
    transferAmount: 0,
    cardAmount: 0,
    netSales: 0,
    beverageAmount: 0,
    foodAmount: 0,
    shishaAmount: 0,
    entertainmentAmount: 0,
    entertainmentBill: 0,
    pendingBill: 0,
    collectionTotalSale: 0,
    transactionCount: 0
  });
  const [loading, setLoading] = useState(false);
  const [dateLoading, setDateLoading] = useState(true);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showReconciliationData, setShowReconciliationData] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Bill comparison states
  const [showBillComparison, setShowBillComparison] = useState(false);
  const [billComparisonData, setBillComparisonData] = useState(null);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [comparisonProgress, setComparisonProgress] = useState({ step: 0, progress: 0, description: '' });

  // Get user information from localStorage (for saving data)
  const userType = localStorage.getItem('usertype') || sessionStorage.getItem('usertype');
  const userName = localStorage.getItem('uname') || sessionStorage.getItem('uname');
  
  // Create userInfo object for backward compatibility
  const userInfo = {
    usertype: userType,
    uname: userName
  };

  // Currency formatter
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  // Fetch default date from API
  const fetchDefaultDate = useCallback(async () => {
    try {
      setDateLoading(true);
      
      // Use the specific last-close-date API endpoint only
      const lastCloseResponse = await getLastCloseDate();
      
      if (lastCloseResponse && lastCloseResponse.success && lastCloseResponse.close_date) {
        setSelectedDate(lastCloseResponse.close_date);
      } else if (lastCloseResponse && lastCloseResponse.data && lastCloseResponse.data.dte) {
        setSelectedDate(lastCloseResponse.data.dte);
      } else {
        // Final fallback to today's date if API doesn't return valid data
        setSelectedDate(new Date().toISOString().split('T')[0]);
      }
    } catch (error) {
      console.error('Error fetching default date:', error);
      // Fallback to today's date
      setSelectedDate(new Date().toISOString().split('T')[0]);
      toast.error('Could not fetch setup date, using today\'s date');
    } finally {
      setDateLoading(false);
    }
  }, []);

  // Fetch sales data for reconciliation using Sales Summary API
  const fetchSalesData = useCallback(async () => {
    setLoading(true);
    try {
      // Use the Sales Summary API with GET method and query parameter
      console.log('Fetching sales summary from API for date:', selectedDate);
      
      const response = await axios.get(`/sales-summary?date=${selectedDate}`, getHeaders());
      
      console.log('Sales summary API response:', response.data);
      
      if (response.data.success && response.data.data) {
        const data = response.data.data;
        
        // Extract data from the comprehensive API response
        const finalBill = data.final_bill_summary;
        const collection = data.collection_summary;
        const salesByCategory = data.sales_by_category;
        
        // Debug: Log the collection object to see all available fields
        console.log('Collection summary object:', collection);
        console.log('Collection keys:', Object.keys(collection));
        
        // Try different possible field names for bank transfer and card
        const transferAmount = collection.bank_transfer || collection.transfer || collection.bankTransfer || 0;
        const cardAmount = collection.card || collection.card_payment || collection.cardPayment || 0;
        
        setSalesData({
          // Main sales metrics from final_bill_summary
          totalSales: finalBill.total_sale || 0,
          totalDiscount: finalBill.total_discount || 0,
          netSales: finalBill.net_sale || 0,
          transactionCount: finalBill.total_bills || 0,
          
          // Entertainment from final bill (separate entertainment bills)
          entertainmentBill: finalBill.entertainment || 0,
          
          // Payment breakdown from collection_summary (includes pending bills)
          cashAmount: collection.cash || 0,
          transferAmount: transferAmount,
          cardAmount: cardAmount,
          pendingBill: collection.pending || 0,
          collectionTotalSale: collection.total_sale || 0, // Total sale from collection table
          
          // Category breakdown from sales_by_category
          // Note: entertainmentAmount is from cart items, entertainmentBill is from separate bills
          // If backend includes both in sales_by_category.entertainment, subtract the separate bills
          beverageAmount: salesByCategory.beverages || 0,
          foodAmount: salesByCategory.food || 0,
          shishaAmount: salesByCategory.shisha || 0,
          entertainmentAmount: (salesByCategory.entertainment || 0) - (finalBill.entertainment || 0) < 0 
            ? 0 
            : (salesByCategory.entertainment || 0) - (finalBill.entertainment || 0)
        });
        
        console.log('Sales data successfully processed:', {
          totalSales: finalBill.total_sale,
          totalDiscount: finalBill.total_discount,
          netSales: finalBill.net_sale,
          cashAmount: collection.cash,
          transferAmount: transferAmount,
          cardAmount: cardAmount,
          pendingBill: collection.pending
        });
        
      } else {
        console.warn('API returned no data or unsuccessful response');
        // Set empty data if no results
        setSalesData({
          totalSales: 0,
          totalDiscount: 0,
          cashAmount: 0,
          transferAmount: 0,
          cardAmount: 0,
          netSales: 0,
          beverageAmount: 0,
          foodAmount: 0,
          shishaAmount: 0,
          entertainmentAmount: 0,
          entertainmentBill: 0,
          pendingBill: 0,
          collectionTotalSale: 0,
          transactionCount: 0
        });
      }
      
    } catch (error) {
      console.error('Error fetching sales summary from API:', error);
      toast.error('Failed to fetch sales data from API');
      
      // Set empty data on error
      setSalesData({
        totalSales: 0,
        totalDiscount: 0,
        cashAmount: 0,
        transferAmount: 0,
        cardAmount: 0,
        netSales: 0,
        beverageAmount: 0,
        foodAmount: 0,
        shishaAmount: 0,
        entertainmentAmount: 0,
        entertainmentBill: 0,
        pendingBill: 0,
        collectionTotalSale: 0,
        transactionCount: 0
      });
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  // Bill comparison functionality
  const runBillComparison = async () => {
    setComparisonLoading(true);
    setComparisonProgress({ step: 1, progress: 20, description: 'Fetching Final Bill data...' });
    
    try {
      // Simulate progressive steps
      const steps = [
        { step: 1, progress: 20, description: 'Fetching Final Bill data...' },
        { step: 2, progress: 40, description: 'Fetching Collection data...' },
        { step: 3, progress: 60, description: 'Cross-referencing invoice numbers...' },
        { step: 4, progress: 80, description: 'Identifying discrepancies...' },
        { step: 5, progress: 100, description: 'Generating report...' }
      ];
      
      // Simulate progress updates
      for (let i = 0; i < steps.length - 1; i++) {
        setComparisonProgress(steps[i]);
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      
      // Run actual comparison
      const response = await axios.post('/bill-comparison/compare', {}, getHeaders());
      
      if (response.data.success) {
        setBillComparisonData(response.data.data);
        setComparisonProgress(steps[4]);
        toast.success('Bill comparison completed successfully!');
      } else {
        throw new Error(response.data.message || 'Bill comparison failed');
      }
      
    } catch (error) {
      console.error('Error running bill comparison:', error);
      toast.error('Failed to run bill comparison: ' + (error.response?.data?.message || error.message));
    } finally {
      setComparisonLoading(false);
    }
  };

  // Get detailed bill information
  const getBillDetails = async (invoiceNumber) => {
    try {
      const response = await axios.get(`/bill-comparison/details/${invoiceNumber}`, getHeaders());
      
      if (response.data.success) {
        // Show bill details in a toast or alert for now
        const details = response.data.data;
        const message = `Invoice ${invoiceNumber}:\n` +
          `Final Bill: ${details.exists_in_final_bill ? 'Found' : 'Missing'}\n` +
          `Collection: ${details.exists_in_collection ? 'Found' : 'Missing'}`;
        toast.info(message);
      }
    } catch (error) {
      console.error('Error fetching bill details:', error);
      toast.error('Failed to fetch bill details');
    }
  };

  // Check if there are discrepancies that need attention
  const hasDiscrepancies = () => {
    if (!billComparisonData) return false;
    return Math.abs(billComparisonData.summary.total_difference) > 0.01 || 
           billComparisonData.summary.total_discrepancies > 0;
  };

  // Validate password for day close
  const validatePassword = () => {
    const correctPassword = 'Real@123';
    if (password === correctPassword) {
      setShowPasswordModal(false);
      setPassword('');
      setPasswordError('');
      setShowConfirmModal(true);
    } else {
      setPasswordError('Incorrect password. Please try again.');
      toast.error('Incorrect password!');
    }
  };

  // Handle password modal close
  const handlePasswordModalClose = () => {
    setShowPasswordModal(false);
    setPassword('');
    setPasswordError('');
  };

  // Save day close summary
  const saveDayCloseSummary = async () => {
    try {
      setIsProcessing(true);
      
      // Prepare morning sale data
      const morningSaleData = {
        sales_date: selectedDate,
        total_sales: salesData.totalSales,
        total_discount: salesData.totalDiscount,
        net_sales: salesData.netSales,
        cash_amount: salesData.cashAmount,
        transfer_amount: salesData.transferAmount,
        card_amount: salesData.cardAmount,
        beverage_amount: salesData.beverageAmount,
        food_amount: salesData.foodAmount,
        shisha_amount: salesData.shishaAmount,
        entertainment_amount: salesData.entertainmentAmount,
        entertainment_bill: salesData.entertainmentBill,
        pending_bill: salesData.pendingBill,
        transaction_count: salesData.transactionCount,
        closed_by: userInfo.uname,
        closed_at: new Date().toISOString()
      };

      // Check if entry already exists for this date
      const existingEntries = await fetchData('morning_sale', null, 'id', {
        sales_date: selectedDate
      });

      if (existingEntries && existingEntries.length > 0) {
        // Update existing entry
        await fetchData('morning_sale', 'PUT', existingEntries[0].id, morningSaleData);
        toast.success('Day close summary updated successfully!');
      } else {
        // Create new entry
        await fetchData('morning_sale', 'POST', null, morningSaleData);
        toast.success('Day close summary saved successfully!');
      }

      // Update sale_date_setup for next day billing
      const nextDate = new Date(selectedDate);
      nextDate.setDate(nextDate.getDate() + 1);
      const nextDateString = nextDate.toISOString().split('T')[0];
      
      const dateUpdateData = {
        current_date: nextDateString,
        updated_by: userInfo.uname,
        updated_at: new Date().toISOString()
      };

      const dateUpdateResponse = await fetchData('sale_date_setup', 'PUT', 1, dateUpdateData);
      
      if (dateUpdateResponse) {
        toast.success('Billing date updated for next day operations!');
      }

      // Close modal and refresh data
      setShowConfirmModal(false);
      fetchSalesData(); // Refresh the display

    } catch (error) {
      console.error('Error saving day close summary:', error);
      toast.error('Failed to save day close summary. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Export functions
  const exportToPDF = () => {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      let yPosition = 15;

      // Format number without currency symbol for PDF
      const formatNumberPDF = (amount) => {
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(amount || 0);
      };

      // Color scheme - smooth and professional (changed from purple to teal/turquoise)
      const colors = {
        primary: [20, 184, 166],       // Teal #14B8A6
        secondary: [59, 130, 246],     // Light blue #3B82F6
        success: [5, 150, 105],        // Green #059669
        warning: [217, 119, 6],        // Orange #D97706
        danger: [234, 88, 12],         // Red #EA580C
        dark: [55, 65, 81],            // Dark gray #374151
        light: [240, 253, 250],        // Very light teal #F0FDFA
        white: [255, 255, 255]
      };

      // Helper function to draw rounded rectangle
      const drawRoundedRect = (x, y, width, height, color, radius = 2) => {
        doc.setFillColor(...color);
        doc.roundedRect(x, y, width, height, radius, radius, 'F');
      };

    // ====== HEADER SECTION ======
    drawRoundedRect(10, yPosition, pageWidth - 20, 30, colors.primary);
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('Day Close Summary Report', pageWidth / 2, yPosition + 12, { align: 'center' });
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text(`Date: ${new Date(selectedDate).toLocaleDateString('en-US', { 
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
    })}`, pageWidth / 2, yPosition + 22, { align: 'center' });
    
    yPosition += 40;

    // ====== SECTION 1: TWO COLUMNS - SUMMARY & PAYMENT BREAKDOWN ======
    doc.setTextColor(...colors.dark);
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text('Financial Overview', 14, yPosition);
    yPosition += 8;

    const columnWidth = (pageWidth - 30) / 2;
    const leftColumnX = 14;
    const rightColumnX = leftColumnX + columnWidth + 5;
    
    // LEFT SIDE: Summary Card Details
    drawRoundedRect(leftColumnX - 2, yPosition - 2, columnWidth, 75, colors.light);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...colors.primary);
    doc.text('Summary Details', leftColumnX + 2, yPosition + 5);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    let leftY = yPosition + 12;
    
    const summaryItems = [
      { label: 'Total Sales (Gross)', value: formatNumberPDF(salesData.totalSales), color: colors.dark },
      { label: 'Total Discount', value: formatNumberPDF(salesData.totalDiscount), color: colors.warning },
      { label: 'Net Sales', value: formatNumberPDF(salesData.netSales), color: colors.success },
      { label: 'Pending Bill', value: formatNumberPDF(salesData.pendingBill), color: colors.danger },
      { label: 'Entertainment Bill', value: formatNumberPDF(salesData.entertainmentBill), color: colors.dark }
    ];
    
    summaryItems.forEach((item, index) => {
      doc.setTextColor(...colors.dark);
      doc.setFont('helvetica', 'normal');
      doc.text(item.label, leftColumnX + 2, leftY);
      
      doc.setTextColor(...item.color);
      doc.setFont('helvetica', 'bold');
      doc.text(item.value, leftColumnX + columnWidth - 4, leftY, { align: 'right' });
      leftY += 6.5;
    });
    
    // Total for left side (Net Sales - Pending Bill - Entertainment Bill)
    const netAmountForCollection = salesData.netSales - salesData.pendingBill;
    leftY += 2;
    doc.setDrawColor(...colors.primary);
    doc.setLineWidth(0.5);
    doc.line(leftColumnX + 2, leftY - 1, leftColumnX + columnWidth - 4, leftY - 1);
    
    doc.setTextColor(...colors.primary);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Net Amount (for Collection)', leftColumnX + 2, leftY + 4);
    doc.text(formatNumberPDF(netAmountForCollection), leftColumnX + columnWidth - 4, leftY + 4, { align: 'right' });

    // RIGHT SIDE: Payment Method Breakdown
    drawRoundedRect(rightColumnX - 2, yPosition - 2, columnWidth, 75, colors.light);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...colors.primary);
    doc.text('Payment Method Breakdown', rightColumnX + 2, yPosition + 5);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    let rightY = yPosition + 12;
    
    const paymentItems = [
      { label: 'Cash Payment', value: formatNumberPDF(salesData.cashAmount), color: colors.success },
      { label: 'Bank Transfer', value: formatNumberPDF(salesData.transferAmount), color: colors.secondary },
      { label: 'Card Payment', value: formatNumberPDF(salesData.cardAmount), color: colors.warning }
    ];
    
    // Add Total Discount to payment breakdown
    paymentItems.push({ 
      label: 'Total Discount', 
      value: formatNumberPDF(salesData.totalDiscount), 
      color: colors.warning 
    });
    
    if (salesData.pendingBill > 0) {
      paymentItems.push({ 
        label: 'Pending Bills', 
        value: formatNumberPDF(salesData.pendingBill), 
        color: colors.danger 
      });
    }
    
    paymentItems.forEach((item, index) => {
      doc.setTextColor(...colors.dark);
      doc.setFont('helvetica', 'normal');
      doc.text(item.label, rightColumnX + 2, rightY);
      
      doc.setTextColor(...item.color);
      doc.setFont('helvetica', 'bold');
      doc.text(item.value, rightColumnX + columnWidth - 4, rightY, { align: 'right' });
      rightY += 6.5;
    });
    
    // Total for right side
    const totalCollection = salesData.cashAmount + salesData.transferAmount + salesData.cardAmount;
    rightY += 2;
    doc.setDrawColor(...colors.primary);
    doc.setLineWidth(0.5);
    doc.line(rightColumnX + 2, rightY - 1, rightColumnX + columnWidth - 4, rightY - 1);
    
    doc.setTextColor(...colors.primary);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Total Collection', rightColumnX + 2, rightY + 4);
    doc.text(formatNumberPDF(totalCollection), rightColumnX + columnWidth - 4, rightY + 4, { align: 'right' });

    yPosition += 80;

    // ====== VERIFICATION BOX - DIFFERENCE CHECK ======
    const netAmountValue = salesData.netSales - salesData.pendingBill;
    const difference = netAmountValue - totalCollection;
    const hasDifference = Math.abs(difference) > 0.01;
    
    if (hasDifference) {
      const bgColor = difference > 0 ? [254, 226, 226] : [220, 252, 231];
      const textColor = difference > 0 ? colors.danger : colors.success;
      drawRoundedRect(14, yPosition, pageWidth - 28, 14, bgColor);
      doc.setTextColor(...textColor);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.text('DIFFERENCE DETECTED', 18, yPosition + 5);
      doc.text(`Net Sales vs Collection: ${formatNumberPDF(Math.abs(difference))} ${difference > 0 ? '(Over)' : '(Under)'}`, 
        18, yPosition + 10);
    } else {
      drawRoundedRect(14, yPosition, pageWidth - 28, 10, [220, 252, 231]);
      doc.setTextColor(...colors.success);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.text('VERIFIED: Amounts Match Perfectly', pageWidth / 2, yPosition + 7, { align: 'center' });
    }
    
    yPosition += (hasDifference ? 18 : 14);

    // ====== SECTION 2: CATEGORY WISE SALE ANALYSIS ======
    doc.setTextColor(...colors.dark);
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text('Category-wise Sales Analysis', 14, yPosition);
    yPosition += 5;

    // Calculate total of all categories for percentage
    const totalCategories = salesData.beverageAmount + salesData.foodAmount + salesData.shishaAmount + salesData.entertainmentAmount;
    
    const categoryData = [
      ['Category', 'Amount', 'Percentage'],
      [
        'Beverages',
        formatNumberPDF(salesData.beverageAmount),
        `${((salesData.beverageAmount / totalCategories) * 100 || 0).toFixed(2)}%`
      ],
      [
        'Food Items',
        formatNumberPDF(salesData.foodAmount),
        `${((salesData.foodAmount / totalCategories) * 100 || 0).toFixed(2)}%`
      ],
      [
        'Shisha & Bar',
        formatNumberPDF(salesData.shishaAmount),
        `${((salesData.shishaAmount / totalCategories) * 100 || 0).toFixed(2)}%`
      ],
      [
        'Entertainment',
        formatNumberPDF(salesData.entertainmentAmount),
        `${((salesData.entertainmentAmount / totalCategories) * 100 || 0).toFixed(2)}%`
      ]
    ];

    doc.autoTable({
      startY: yPosition,
      head: [categoryData[0]],
      body: categoryData.slice(1),
      theme: 'grid',
      headStyles: {
        fillColor: colors.primary,
        textColor: colors.white,
        fontSize: 10,
        fontStyle: 'bold',
        halign: 'center'
      },
      bodyStyles: {
        fontSize: 9,
        textColor: colors.dark
      },
      alternateRowStyles: {
        fillColor: colors.light
      },
      columnStyles: {
        0: { cellWidth: 60, halign: 'left' },
        1: { cellWidth: 70, halign: 'right', fontStyle: 'bold' },
        2: { cellWidth: 40, halign: 'center' }
      },
      margin: { left: 14, right: 14 }
    });

    yPosition = doc.lastAutoTable.finalY + 15;

    // ====== SIGNATURE SECTION ======
    // Draw signature line box
    drawRoundedRect(14, yPosition, pageWidth - 28, 42, colors.light);
    
    yPosition += 4;
    doc.setTextColor(...colors.dark);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Authorized Signatures', pageWidth / 2, yPosition, { align: 'center' });
    
    yPosition += 8;
    
    const signatureWidth = (pageWidth - 40) / 3;
    const signaturePositions = [
      { x: 20, label: 'Cashier' },
      { x: 20 + signatureWidth + 5, label: 'Accountant' },
      { x: 20 + (signatureWidth + 5) * 2, label: 'Manager' }
    ];
    
    signaturePositions.forEach(sig => {
      // Signature line
      doc.setDrawColor(...colors.dark);
      doc.setLineWidth(0.3);
      doc.line(sig.x, yPosition + 12, sig.x + signatureWidth - 10, yPosition + 12);
      
      // Label
      doc.setTextColor(...colors.dark);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.text(sig.label, sig.x + (signatureWidth - 10) / 2, yPosition + 17, { align: 'center' });
      
      // Name and Date labels
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(100, 100, 100);
      doc.text('Name: _____________', sig.x, yPosition + 22);
      doc.text('Date: _____________', sig.x, yPosition + 27);
    });

    // ====== FOOTER ======
    const footerY = pageHeight - 12;
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.line(14, footerY, pageWidth - 14, footerY);
    
    doc.setTextColor(120, 120, 120);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'italic');
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, footerY + 4);
    doc.text(`By: ${userName || 'System'}`, pageWidth - 14, footerY + 4, { align: 'right' });
    doc.text('Page 1 of 1', pageWidth / 2, footerY + 4, { align: 'center' });

    // Save the PDF
    doc.save(`day-close-${selectedDate}.pdf`);
    toast.success('PDF exported successfully!');
    } catch (error) {
      console.error('PDF Export Error:', error);
      toast.error(`Failed to export PDF: ${error.message}`);
    }
  };

  const exportReconciliationToPDF = () => {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      let yPosition = 15;

      // Format number without currency symbol for PDF
      const formatNumberPDF = (amount) => {
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(amount || 0);
      };

      // Color scheme
      const colors = {
        primary: [20, 184, 166],
        secondary: [59, 130, 246],
        success: [5, 150, 105],
        warning: [217, 119, 6],
        danger: [234, 88, 12],
        info: [14, 165, 233],
        dark: [55, 65, 81],
        light: [240, 253, 250],
        white: [255, 255, 255]
      };

      // Helper function
      const drawRoundedRect = (x, y, width, height, color, radius = 2) => {
        doc.setFillColor(...color);
        doc.roundedRect(x, y, width, height, radius, radius, 'F');
      };

      // ====== HEADER ======
      drawRoundedRect(10, yPosition, pageWidth - 20, 30, colors.primary);
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.text('Sales Reconciliation Report', pageWidth / 2, yPosition + 12, { align: 'center' });
      
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.text(`Date: ${new Date(selectedDate).toLocaleDateString('en-US', { 
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
      })}`, pageWidth / 2, yPosition + 22, { align: 'center' });
      
      yPosition += 38;

      // ====== SALES SUMMARY TABLE ======
      doc.setTextColor(...colors.dark);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Sales Summary', 14, yPosition);
      yPosition += 2;

      const salesSummary = [
        ['Metric', 'Amount', 'Percentage', 'Status'],
        ['Total Sales (Gross)', formatNumberPDF(salesData.totalSales), '100.00%', 'Complete'],
        ['Less: Total Discount', formatNumberPDF(salesData.totalDiscount), 
          `${((salesData.totalDiscount / salesData.totalSales) * 100 || 0).toFixed(2)}%`, 'Applied'],
        ['Net Sales (After Discount)', formatNumberPDF(salesData.netSales), 
          `${((salesData.netSales / salesData.totalSales) * 100 || 0).toFixed(2)}%`, 'Final'],
        ['Total Transactions', salesData.transactionCount.toString(), '-', `${salesData.transactionCount} bills`]
      ];

      doc.autoTable({
        startY: yPosition,
        head: [salesSummary[0]],
        body: salesSummary.slice(1),
        theme: 'grid',
        headStyles: {
          fillColor: colors.primary,
          textColor: colors.white,
          fontSize: 9,
          fontStyle: 'bold'
        },
        bodyStyles: {
          fontSize: 8,
          textColor: colors.dark
        },
        columnStyles: {
          0: { cellWidth: 60 },
          1: { cellWidth: 40, halign: 'right', fontStyle: 'bold' },
          2: { cellWidth: 30, halign: 'center' },
          3: { cellWidth: 40, halign: 'center' }
        }
      });

      yPosition = doc.lastAutoTable.finalY + 10;

      // ====== PAYMENT COLLECTION TABLE ======
      doc.setTextColor(...colors.dark);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Payment Collection Summary', 14, yPosition);
      yPosition += 2;

      const totalCollection = salesData.cashAmount + salesData.transferAmount + salesData.cardAmount;
      const paymentSummary = [
        ['Payment Method', 'Amount Collected', '% of Collection', 'Status'],
        ['Cash Payment', formatNumberPDF(salesData.cashAmount), 
          `${((salesData.cashAmount / totalCollection) * 100 || 0).toFixed(2)}%`, 'Collected'],
        ['Bank Transfer', formatNumberPDF(salesData.transferAmount), 
          `${((salesData.transferAmount / totalCollection) * 100 || 0).toFixed(2)}%`, 'Collected'],
        ['Card Payment', formatNumberPDF(salesData.cardAmount), 
          `${((salesData.cardAmount / totalCollection) * 100 || 0).toFixed(2)}%`, 'Collected'],
        ['Total Collection', formatNumberPDF(totalCollection), '100.00%', 'Complete']
      ];

      if (salesData.pendingBill > 0) {
        paymentSummary.splice(4, 0, ['Pending Bills', formatNumberPDF(salesData.pendingBill), '-', 'Outstanding']);
      }

      doc.autoTable({
        startY: yPosition,
        head: [paymentSummary[0]],
        body: paymentSummary.slice(1),
        theme: 'grid',
        headStyles: {
          fillColor: colors.primary,
          textColor: colors.white,
          fontSize: 9,
          fontStyle: 'bold'
        },
        bodyStyles: {
          fontSize: 8,
          textColor: colors.dark
        },
        columnStyles: {
          0: { cellWidth: 60 },
          1: { cellWidth: 40, halign: 'right', fontStyle: 'bold' },
          2: { cellWidth: 30, halign: 'center' },
          3: { cellWidth: 40, halign: 'center' }
        }
      });

      yPosition = doc.lastAutoTable.finalY + 10;

      // ====== CATEGORY SALES TABLE ======
      doc.setTextColor(...colors.dark);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Category-wise Sales Breakdown', 14, yPosition);
      yPosition += 2;

      const totalCategories = salesData.beverageAmount + salesData.foodAmount + salesData.shishaAmount + salesData.entertainmentAmount;
      const categorySummary = [
        ['Category', 'Sales Amount', '% of Total', 'Performance'],
        ['Beverages', formatNumberPDF(salesData.beverageAmount), 
          `${((salesData.beverageAmount / totalCategories) * 100 || 0).toFixed(2)}%`, 'Active'],
        ['Food Items', formatNumberPDF(salesData.foodAmount), 
          `${((salesData.foodAmount / totalCategories) * 100 || 0).toFixed(2)}%`, 'Active'],
        ['Shisha & Bar', formatNumberPDF(salesData.shishaAmount), 
          `${((salesData.shishaAmount / totalCategories) * 100 || 0).toFixed(2)}%`, 'Active'],
        ['Entertainment (Cart)', formatNumberPDF(salesData.entertainmentAmount), 
          `${((salesData.entertainmentAmount / totalCategories) * 100 || 0).toFixed(2)}%`, 'Active']
      ];

      if (salesData.entertainmentBill > 0) {
        categorySummary.push(['Entertainment (Bills)', formatNumberPDF(salesData.entertainmentBill), '-', 'Separate']);
      }

      doc.autoTable({
        startY: yPosition,
        head: [categorySummary[0]],
        body: categorySummary.slice(1),
        theme: 'grid',
        headStyles: {
          fillColor: colors.primary,
          textColor: colors.white,
          fontSize: 9,
          fontStyle: 'bold'
        },
        bodyStyles: {
          fontSize: 8,
          textColor: colors.dark
        },
        columnStyles: {
          0: { cellWidth: 60 },
          1: { cellWidth: 40, halign: 'right', fontStyle: 'bold' },
          2: { cellWidth: 30, halign: 'center' },
          3: { cellWidth: 40, halign: 'center' }
        }
      });

      yPosition = doc.lastAutoTable.finalY + 10;

      // ====== RECONCILIATION SUMMARY ======
      drawRoundedRect(14, yPosition, pageWidth - 28, 30, colors.light);
      
      doc.setTextColor(...colors.dark);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Reconciliation Status', pageWidth / 2, yPosition + 6, { align: 'center' });
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...colors.success);
      doc.text('Records Match - Sales data successfully reconciled', pageWidth / 2, yPosition + 13, { align: 'center' });
      
      doc.setTextColor(...colors.dark);
      doc.text(`Date: ${new Date(selectedDate).toLocaleDateString()}`, pageWidth / 2, yPosition + 19, { align: 'center' });
      doc.text(`Bills: ${salesData.transactionCount} | Net Sales: ${formatNumberPDF(salesData.netSales)}`, 
        pageWidth / 2, yPosition + 25, { align: 'center' });

      // ====== FOOTER ======
      const footerY = pageHeight - 12;
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.3);
      doc.line(14, footerY, pageWidth - 14, footerY);
      
      doc.setTextColor(120, 120, 120);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'italic');
      doc.text(`Generated: ${new Date().toLocaleString()}`, 14, footerY + 4);
      doc.text(`By: ${userName || 'System'}`, pageWidth - 14, footerY + 4, { align: 'right' });
      doc.text('Reconciliation Report - Page 1', pageWidth / 2, footerY + 4, { align: 'center' });

      // Save the PDF
      doc.save(`sales-reconciliation-${selectedDate}.pdf`);
      toast.success('Reconciliation PDF exported successfully!');
    } catch (error) {
      console.error('Reconciliation PDF Export Error:', error);
      toast.error(`Failed to export reconciliation PDF: ${error.message}`);
    }
  };

  const exportToExcel = () => {
    const workbook = XLSX.utils.book_new();
    
    // Summary sheet
    const summaryData = [
      ['Day Close Summary', ''],
      ['Date', new Date(selectedDate).toLocaleDateString()],
      ['', ''],
      ['Total Sales', salesData.totalSales],
      ['Total Discount', salesData.totalDiscount],
      ['Net Sales', salesData.netSales],
      ['Entertainment Bill', salesData.entertainmentBill],
      ['Pending Bills', salesData.pendingBill],
      ['Transactions', salesData.transactionCount],
      ['', ''],
      ['Payment Methods', ''],
      ['Cash', salesData.cashAmount],
      ['Transfer', salesData.transferAmount],
      ['Card', salesData.cardAmount],
      ['', ''],
      ['Categories', ''],
      ['Beverage', salesData.beverageAmount],
      ['Food', salesData.foodAmount],
      ['Shisha', salesData.shishaAmount],
      ['Entertainment', salesData.entertainmentAmount]
    ];
    
    const worksheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Day Close Summary');
    
    XLSX.writeFile(workbook, `day-close-${selectedDate}.xlsx`);
    toast.success('Excel file exported successfully!');
  };

  const generateThermalPrint = () => {
    const printWindow = window.open('', '_blank');
    const printContent = `
      <html>
        <head>
          <title>Day Close - ${selectedDate}</title>
          <style>
            body { font-family: 'Courier New', monospace; font-size: 12px; margin: 10px; }
            .header { text-align: center; border-bottom: 1px dashed #000; padding-bottom: 10px; margin-bottom: 10px; }
            .line { border-bottom: 1px dashed #000; margin: 10px 0; }
            .total { font-weight: bold; font-size: 14px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h2>DAY CLOSE SUMMARY</h2>
            <div>${new Date(selectedDate).toLocaleDateString()}</div>
          </div>
          <div>Total Sales: ${formatCurrency(salesData.totalSales)}</div>
          <div>Total Discount: ${formatCurrency(salesData.totalDiscount)}</div>
          <div>Net Sales: ${formatCurrency(salesData.netSales)}</div>
          <div>Entertainment Bill: ${formatCurrency(salesData.entertainmentBill)}</div>
          <div>Pending Bills: ${formatCurrency(salesData.pendingBill)}</div>
          <div>Transactions: ${salesData.transactionCount}</div>
          <div class="line"></div>
          <div>Cash: ${formatCurrency(salesData.cashAmount)}</div>
          <div>Transfer: ${formatCurrency(salesData.transferAmount)}</div>
          <div>Card: ${formatCurrency(salesData.cardAmount)}</div>
          <div class="line"></div>
          <div>Beverage: ${formatCurrency(salesData.beverageAmount)}</div>
          <div>Food: ${formatCurrency(salesData.foodAmount)}</div>
          <div>Shisha: ${formatCurrency(salesData.shishaAmount)}</div>
          <div>Entertainment: ${formatCurrency(salesData.entertainmentAmount)}</div>
        </body>
      </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
    toast.success('Print initiated successfully!');
  };

  useEffect(() => {
    // Since PrivateRoute already handles authentication, just fetch default date
    fetchDefaultDate();
  }, [fetchDefaultDate]);

  useEffect(() => {
    if (selectedDate && !dateLoading) {
      fetchSalesData();
    }
  }, [selectedDate, dateLoading, fetchSalesData]);

  return (
    <Layout>
      <Container fluid className="day-close-modern">
        <ToastContainer />
        
        {/* Modern Header Section */}
        <div className="modern-header">
          <div className="header-content">
            <div className="header-left">
              <h1 className="page-title">
                <FaCalendarDay className="title-icon" />
                Day Close Dashboard
              </h1>
              <p className="page-subtitle">Sales reconciliation and end-of-day operations</p>
            </div>
            
            <div className="header-right">
              <div className="date-selector-wrapper">
                
                <Form.Control
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="modern-date-input"
                  disabled={dateLoading}
                />
              </div>
              
              <div className="action-buttons">
                <Button
                  variant={showReconciliationData ? "success" : "outline-info"}
                  className="export-btn reconciliation-btn"
                  onClick={() => setShowReconciliationData(!showReconciliationData)}
                  disabled={loading || salesData.transactionCount === 0}
                >
                  📊 {showReconciliationData ? 'Hide' : 'Show'} Sales Reconciliation
                </Button>
                {billComparisonData && hasDiscrepancies() && (
                  <Button
                    variant="warning"
                    className="export-btn comparison-alert-btn"
                    onClick={() => setShowBillComparison(true)}
                    disabled={loading}
                  >
                    ⚠️ Bill Issues Found
                  </Button>
                )}
                <Button
                  variant="danger"
                  className="export-btn pdf-btn"
                  onClick={exportToPDF}
                  disabled={loading || salesData.transactionCount === 0}
                >
                  <FaFilePdf /> PDF Summary
                </Button>
                <Button
                  variant="info"
                  className="export-btn pdf-reconciliation-btn"
                  onClick={exportReconciliationToPDF}
                  disabled={loading || salesData.transactionCount === 0}
                >
                  <FaFilePdf /> PDF Reconciliation
                </Button>
                <Button
                  variant="success"
                  className="export-btn excel-btn"
                  onClick={exportToExcel}
                  disabled={loading || salesData.transactionCount === 0}
                >
                  <FaFileExcel /> Excel
                </Button>
                
                <Button
                  variant="outline-secondary"
                  className="export-btn thermal-btn"
                  onClick={generateThermalPrint}
                  disabled={loading || salesData.transactionCount === 0}
                >
                  🖨️ Thermal
                </Button>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="loading-state">
            <Spinner animation="border" className="loading-spinner" />
            <h4>Loading sales data...</h4>
            <p>Please wait while we gather today's transaction information</p>
          </div>
        ) : (
          <div className="dashboard-content">
            {salesData.transactionCount === 0 ? (
              <div className="empty-state">
                <FaExclamationTriangle className="empty-icon" />
                <h3>No Sales Data Available</h3>
                <p>No transactions were found for the selected date.</p>
                <small>Try selecting a different date or check if any sales were recorded.</small>
              </div>
            ) : (
              <>
                {/* Date Banner */}
                <div className="date-banner">
                  <div className="date-info">
                    <FaCalendarAlt className="date-icon" />
                    <div>
                      <h2>{new Date(selectedDate).toLocaleDateString('en-US', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}</h2>
                      <span className="transaction-count">{salesData.transactionCount} Transactions Recorded</span>
                    </div>
                  </div>
                </div>

                {/* Key Metrics Dashboard */}
                <div className="metrics-dashboard">
                  <Row className="metrics-row">
                    <Col xl={2} lg={2} md={4} sm={6} xs={12}>
                      <div className="metric-card total-sales">
                        <div className="metric-icon-wrapper">
                          <FaMoneyBillWave className="metric-icon" />
                        </div>
                        <div className="metric-content">
                          <span className="metric-label">Total Sales</span>
                          <h3 className="metric-value">{formatCurrency(salesData.totalSales)}</h3>
                        </div>
                        <div className="metric-trend positive">📈</div>
                      </div>
                    </Col>
                    
                    <Col xl={2} lg={2} md={4} sm={6} xs={12}>
                      <div className="metric-card total-discount">
                        <div className="metric-icon-wrapper">
                          <FaMoneyBillAlt className="metric-icon" />
                        </div>
                        <div className="metric-content">
                          <span className="metric-label">Total Discount</span>
                          <h3 className="metric-value">{formatCurrency(salesData.totalDiscount)}</h3>
                        </div>
                        <div className="metric-trend neutral">💰</div>
                      </div>
                    </Col>
                    
                    <Col xl={2} lg={2} md={4} sm={6} xs={12}>
                      <div className="metric-card net-sales">
                        <div className="metric-icon-wrapper">
                          <FaCheckCircle className="metric-icon" />
                        </div>
                        <div className="metric-content">
                          <span className="metric-label">Net Sales</span>
                          <h3 className="metric-value">{formatCurrency(salesData.netSales)}</h3>
                        </div>
                        <div className="metric-trend positive">✅</div>
                      </div>
                    </Col>
                    
                    <Col xl={2} lg={2} md={4} sm={6} xs={12}>
                      <div className="metric-card entertainment-bill">
                        <div className="metric-icon-wrapper">
                          <FaMoneyBillWave className="metric-icon" />
                        </div>
                        <div className="metric-content">
                          <span className="metric-label">Entertainment Bill</span>
                          <h3 className="metric-value">{formatCurrency(salesData.entertainmentBill)}</h3>
                        </div>
                        <div className="metric-trend positive">🎭</div>
                      </div>
                    </Col>
                    
                   
                    
                    <Col xl={2} lg={2} md={4} sm={6} xs={12}>
                      <div className="metric-card transactions">
                        <div className="metric-icon-wrapper">
                          <FaCalendarAlt className="metric-icon" />
                        </div>
                        <div className="metric-content">
                          <span className="metric-label">Transactions</span>
                          <h3 className="metric-value">{salesData.transactionCount}</h3>
                        </div>
                        <div className="metric-trend positive">📊</div>
                      </div>
                    </Col>
                  </Row>
                </div>

                {/* Payment Methods Table */}
                <div className="section-card payment-methods">
                  <div className="section-header">
                    <h4>💳 Payment Method Breakdown</h4>
                    <span className="section-subtitle">Daily payment collection by method</span>
                  </div>
                  
                  <div className="table-responsive">
                    <table className="table table-hover payment-table">
                      <thead>
                        <tr>
                          <th style={{ color: 'white', backgroundColor: '#6B7FD7', borderRadius: '5px 0 0 0', width: '60px', textAlign: 'center' }}>Icon</th>
                          <th style={{ color: 'white', backgroundColor: '#6B7FD7' }}>Payment Method</th>
                          <th style={{ color: 'white', backgroundColor: '#6B7FD7', borderRadius: '0 5px 0 0', textAlign: 'right' }}>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="payment-row cash-row">
                          <td style={{ textAlign: 'center', fontSize: '1.5rem' }}>💵</td>
                          <td>
                            <strong style={{ color: '#059669' }}>Cash</strong>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <strong style={{ color: '#059669', fontSize: '1.1rem' }}>
                              {formatCurrency(salesData.cashAmount)}
                            </strong>
                          </td>
                        </tr>
                        <tr className="payment-row transfer-row">
                          <td style={{ textAlign: 'center', fontSize: '1.5rem' }}>🏦</td>
                          <td>
                            <strong style={{ color: '#1D4ED8' }}>Bank Transfer</strong>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <strong style={{ color: '#1D4ED8', fontSize: '1.1rem' }}>
                              {formatCurrency(salesData.transferAmount)}
                            </strong>
                          </td>
                        </tr>
                        <tr className="payment-row card-row">
                          <td style={{ textAlign: 'center', fontSize: '1.5rem' }}>💳</td>
                          <td>
                            <strong style={{ color: '#D97706' }}>Card Payment</strong>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <strong style={{ color: '#D97706', fontSize: '1.1rem' }}>
                              {formatCurrency(salesData.cardAmount)}
                            </strong>
                          </td>
                        </tr>
                        <tr className="payment-row total-row" style={{ backgroundColor: '#F0F4FF', borderTop: '2px solid #6B7FD7' }}>
                          <td style={{ textAlign: 'center', fontSize: '1.5rem' }}>💰</td>
                          <td>
                            <strong style={{ color: '#6B7FD7', fontSize: '1.1rem' }}>Total Collection</strong>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <strong style={{ color: '#6B7FD7', fontSize: '1.3rem' }}>
                              {formatCurrency(salesData.cashAmount + salesData.transferAmount + salesData.cardAmount)}
                            </strong>
                          </td>
                        </tr>
                        {salesData.pendingBill > 0 && (
                          <tr className="payment-row pending-row">
                            <td style={{ textAlign: 'center', fontSize: '1.5rem' }}>⏳</td>
                            <td>
                              <strong style={{ color: '#EA580C' }}>Pending Bills</strong>
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              <strong style={{ color: '#EA580C', fontSize: '1.1rem' }}>
                                {formatCurrency(salesData.pendingBill)}
                              </strong>
                            </td>
                          </tr>
                        )}
                        <tr className="payment-row discount-row">
                          <td style={{ textAlign: 'center', fontSize: '1.5rem' }}>🏷️</td>
                          <td>
                            <strong style={{ color: '#DC2626' }}>Total Discount</strong>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <strong style={{ color: '#DC2626', fontSize: '1.1rem' }}>
                              {formatCurrency(salesData.totalDiscount)}
                            </strong>
                          </td>
                        </tr>
                        <tr className="payment-row sale-row" style={{ backgroundColor: '#EFF6FF', borderTop: '2px solid #3B82F6' }}>
                          <td style={{ textAlign: 'center', fontSize: '1.5rem' }}>📊</td>
                          <td>
                            <strong style={{ color: '#3B82F6', fontSize: '1.05rem' }}>Total Sale (Collection)</strong>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <strong style={{ color: '#3B82F6', fontSize: '1.2rem' }}>
                              {formatCurrency(salesData.collectionTotalSale - salesData.totalDiscount)}
                            </strong>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Category Analysis */}
                <div className="section-card category-analysis">
                  <div className="section-header">
                    <h4>🍽️ Sales Category Analysis</h4>
                    <span className="section-subtitle">Revenue breakdown by product categories</span>
                  </div>
                  
                  <Row className="category-row">
                    <Col lg={3} md={6} sm={12}>
                      <div className="category-card beverage">
                        <div className="category-icon">🥤</div>
                        <div className="category-content">
                          <h5>Bar</h5>
                          <span className="category-amount">{formatCurrency(salesData.beverageAmount)}</span>
                          <div className="category-bar">
                            <div className="category-fill beverage-fill" style={{width: `${(salesData.beverageAmount / salesData.totalSales * 100) || 0}%`}}></div>
                          </div>
                        </div>
                      </div>
                    </Col>
                    
                    <Col lg={3} md={6} sm={12}>
                      <div className="category-card food">
                        <div className="category-icon">🍕</div>
                        <div className="category-content">
                          <h5>Food Items</h5>
                          <span className="category-amount">{formatCurrency(salesData.foodAmount)}</span>
                          <div className="category-bar">
                            <div className="category-fill food-fill" style={{width: `${(salesData.foodAmount / salesData.totalSales * 100) || 0}%`}}></div>
                          </div>
                        </div>
                      </div>
                    </Col>
                    
                    <Col lg={3} md={6} sm={12}>
                      <div className="category-card shisha">
                        <div className="category-icon">💨</div>
                        <div className="category-content">
                          <h5>Shisha </h5>
                          <span className="category-amount">{formatCurrency(salesData.shishaAmount)}</span>
                          <div className="category-bar">
                            <div className="category-fill shisha-fill" style={{width: `${(salesData.shishaAmount / salesData.totalSales * 100) || 0}%`}}></div>
                          </div>
                        </div>
                      </div>
                    </Col>
                    
                    <Col lg={3} md={6} sm={12}>
                      <div className="category-card entertainment">
                        <div className="category-icon">🎭</div>
                        <div className="category-content">
                          <h5>Entertainment</h5>
                          <span className="category-amount">{formatCurrency(salesData.entertainmentAmount)}</span>
                          <div className="category-bar">
                            <div className="category-fill entertainment-fill" style={{width: `${(salesData.entertainmentAmount / salesData.totalSales * 100) || 0}%`}}></div>
                          </div>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>

                {/* Sales Reconciliation Data Section */}
                {showReconciliationData && (
                  <div className="section-card reconciliation-data">
                    <div className="section-header">
                      <h4>📊 Sales Reconciliation Report</h4>
                      <span className="section-subtitle">Detailed breakdown of sales data for {new Date(selectedDate).toLocaleDateString()}</span>
                    </div>
                    
                    {/* Sales Summary Table */}
                    <div className="reconciliation-table-section">
                      <h5>📈 Sales Summary</h5>
                      <div className="table-responsive">
                        <table className="reconciliation-table">
                          <thead>
                            <tr>
                              <th>Metric</th>
                              <th>Amount (THB)</th>
                              <th>Percentage</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td><strong>Total Sales (Gross)</strong></td>
                              <td className="amount-cell">{formatCurrency(salesData.totalSales)}</td>
                              <td>100.00%</td>
                              <td><span className="status-badge success">✅ Complete</span></td>
                            </tr>
                            <tr>
                              <td>Less: Total Discount</td>
                              <td className="amount-cell discount">{formatCurrency(salesData.totalDiscount)}</td>
                              <td>{((salesData.totalDiscount / salesData.totalSales) * 100 || 0).toFixed(2)}%</td>
                              <td><span className="status-badge warning">💰 Applied</span></td>
                            </tr>
                            <tr className="highlight-row">
                              <td><strong>Net Sales (After Discount)</strong></td>
                              <td className="amount-cell net">{formatCurrency(salesData.netSales)}</td>
                              <td>{((salesData.netSales / salesData.totalSales) * 100 || 0).toFixed(2)}%</td>
                              <td><span className="status-badge success">✅ Final</span></td>
                            </tr>
                            <tr>
                              <td>Total Transactions</td>
                              <td className="amount-cell">{salesData.transactionCount} bills</td>
                              <td>-</td>
                              <td><span className="status-badge info">📊 Count</span></td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Payment Collection Table */}
                    <div className="reconciliation-table-section">
                      <h5>💳 Payment Collection Summary</h5>
                      <div className="table-responsive">
                        <table className="reconciliation-table">
                          <thead>
                            <tr>
                              <th>Payment Method</th>
                              <th>Amount Collected (THB)</th>
                              <th>Percentage of Collection</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td>💵 Cash Payment</td>
                              <td className="amount-cell">{formatCurrency(salesData.cashAmount)}</td>
                              <td>{(((salesData.cashAmount) / (salesData.cashAmount + salesData.transferAmount + salesData.cardAmount)) * 100 || 0).toFixed(2)}%</td>
                              <td><span className="status-badge success">✅ Collected</span></td>
                            </tr>
                            <tr>
                              <td>🏦 Bank Transfer</td>
                              <td className="amount-cell">{formatCurrency(salesData.transferAmount)}</td>
                              <td>{(((salesData.transferAmount) / (salesData.cashAmount + salesData.transferAmount + salesData.cardAmount)) * 100 || 0).toFixed(2)}%</td>
                              <td><span className="status-badge success">✅ Collected</span></td>
                            </tr>
                            <tr>
                              <td>💳 Card Payment</td>
                              <td className="amount-cell">{formatCurrency(salesData.cardAmount)}</td>
                              <td>{(((salesData.cardAmount) / (salesData.cashAmount + salesData.transferAmount + salesData.cardAmount)) * 100 || 0).toFixed(2)}%</td>
                              <td><span className="status-badge success">✅ Collected</span></td>
                            </tr>
                            <tr className="highlight-row">
                              <td><strong>💰 Total Collection</strong></td>
                              <td className="amount-cell total">{formatCurrency(salesData.cashAmount + salesData.transferAmount + salesData.cardAmount)}</td>
                              <td>100.00%</td>
                              <td><span className="status-badge success">✅ Complete</span></td>
                            </tr>
                            {salesData.pendingBill > 0 && (
                              <tr className="pending-row">
                                <td>⏳ Pending Bills</td>
                                <td className="amount-cell pending">{formatCurrency(salesData.pendingBill)}</td>
                                <td>-</td>
                                <td><span className="status-badge danger">⚠️ Outstanding</span></td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Category Sales Table */}
                    <div className="reconciliation-table-section">
                      <h5>🍽️ Category-wise Sales Breakdown</h5>
                      <div className="table-responsive">
                        <table className="reconciliation-table">
                          <thead>
                            <tr>
                              <th>Category</th>
                              <th>Sales Amount (THB)</th>
                              <th>Percentage of Total</th>
                              <th>Performance</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td>🥤 Bar</td>
                              <td className="amount-cell">{formatCurrency(salesData.beverageAmount)}</td>
                              <td>{((salesData.beverageAmount / salesData.totalSales) * 100 || 0).toFixed(2)}%</td>
                              <td><span className="status-badge info">📈 Active</span></td>
                            </tr>
                            <tr>
                              <td>🍕 Food Items</td>
                              <td className="amount-cell">{formatCurrency(salesData.foodAmount)}</td>
                              <td>{((salesData.foodAmount / salesData.totalSales) * 100 || 0).toFixed(2)}%</td>
                              <td><span className="status-badge info">📈 Active</span></td>
                            </tr>
                            <tr>
                              <td>💨 Shisha </td>
                              <td className="amount-cell">{formatCurrency(salesData.shishaAmount)}</td>
                              <td>{((salesData.shishaAmount / salesData.totalSales) * 100 || 0).toFixed(2)}%</td>
                              <td><span className="status-badge info">📈 Active</span></td>
                            </tr>
                            {salesData.entertainmentBill > 0 && (
                              <tr>
                                <td>🎭 Entertainment (Bills)</td>
                                <td className="amount-cell">{formatCurrency(salesData.entertainmentBill)}</td>
                                <td>-</td>
                                <td><span className="status-badge special">🎯 Separate</span></td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Reconciliation Summary */}
                    <div className="reconciliation-summary-section">
                      <div className="summary-cards">
                        <div className="summary-card reconciliation-match">
                          <h6>💯 Reconciliation Status</h6>
                          <div className="match-status">
                            <span className="status-icon">✅</span>
                            <span className="status-text">Records Match</span>
                          </div>
                          <small>Sales data successfully reconciled</small>
                        </div>
                        
                        <div className="summary-card collection-status">
                          <h6>💰 Collection Status</h6>
                          <div className="collection-info">
                            <div>Collected: {formatCurrency(salesData.cashAmount + salesData.transferAmount + salesData.cardAmount)}</div>
                            {salesData.pendingBill > 0 && (
                              <div className="pending-info">Pending: {formatCurrency(salesData.pendingBill)}</div>
                            )}
                          </div>
                        </div>
                        
                        <div className="summary-card day-summary">
                          <h6>📊 Day Summary</h6>
                          <div className="day-info">
                            <div>Date: {new Date(selectedDate).toLocaleDateString()}</div>
                            <div>Bills: {salesData.transactionCount}</div>
                            <div>Net Sales: {formatCurrency(salesData.netSales)}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Bill Comparison Analysis Section - Hidden as per user request */}

                {/* Day Close Action */}
                <div className="section-card day-close-action">
                  <div className="action-content">
                    <div className="action-info">
                      <h4>🔒 Close Day Operations</h4>
                      <p>Complete the day close process to save summary data and prepare for the next business day.</p>
                    </div>
                    <Button
                      variant="success"
                      size="lg"
                      className="close-day-btn"
                      onClick={() => setShowPasswordModal(true)}
                      disabled={loading}
                    >
                      Complete Day Close
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Password Modal */}
        <Modal show={showPasswordModal} onHide={handlePasswordModalClose} centered>
          <Modal.Header closeButton>
            <Modal.Title>🔐 Authentication Required</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Alert variant="info">
              <FaExclamationTriangle className="me-2" />
              Please enter the password to proceed with day close operation.
            </Alert>
            <Form.Group className="mb-3">
              <Form.Label>Password</Form.Label>
              <Form.Control
                type="password"
                placeholder="Enter password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setPasswordError('');
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    validatePassword();
                  }
                }}
                isInvalid={!!passwordError}
                autoFocus
              />
              <Form.Control.Feedback type="invalid">
                {passwordError}
              </Form.Control.Feedback>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handlePasswordModalClose}>
              Cancel
            </Button>
            <Button variant="success" onClick={validatePassword}>
              Verify & Continue
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Confirmation Modal */}
        <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Day Close</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Alert variant="warning">
              <FaExclamationTriangle className="me-2" />
              Are you sure you want to close the day for <strong>{new Date(selectedDate).toLocaleDateString()}</strong>?
            </Alert>
            <p>This action will:</p>
            <ul>
              <li>Save the day's sales summary to morning sale records</li>
              <li>Update the billing date for tomorrow's operations</li>
              <li>Lock the current day's transactions</li>
            </ul>
            <p><strong>This action cannot be undone easily.</strong></p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
              Cancel
            </Button>
            <Button 
              variant="success" 
              onClick={saveDayCloseSummary}
              disabled={isProcessing}
            >
              {isProcessing ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Processing...
                </>
              ) : (
                'Confirm Day Close'
              )}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Floating Navigation Modal */}
        <FloatingNavModal currentPage="day-close" />
      </Container>
    </Layout>
  );
};

export default DayClose;